import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import ArticleContent from '@/components/feature/ArticleContent';
import {
  formatArticleDate,
  getArticleBySlug,
  getVisitCounts,
  listPublishedArticles,
  readingMinutes,
  recordBlogVisit,
  tagList,
  type BlogArticle,
} from '@/lib/blog';

const FALLBACK_COVER =
  'https://readdy.ai/api/search-image?query=Soft%20editorial%20still%20life%20of%20a%20desk%20with%20paper%20documents%20and%20warm%20beige%20tones%2C%20minimal%20modern%20photography%2C%20subtle%20shadows&width=1200&height=600&seq=blog-article-hero-2026&orientation=landscape';

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [related, setRelated] = useState<BlogArticle[]>([]);
  const [views, setViews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const found = await getArticleBySlug(slug);
        if (!alive) return;
        setArticle(found);
        if (found) {
          try {
            const dedupeKey = `zifek-blog-view-${found.id}`;
            const alreadySeen = sessionStorage.getItem(dedupeKey);
            if (!alreadySeen) {
              sessionStorage.setItem(dedupeKey, '1');
              await recordBlogVisit(found.id, `/blog/${slug}`);
            }
          } catch {
            await recordBlogVisit(found.id, `/blog/${slug}`);
          }
          const counts = await getVisitCounts();
          if (alive) setViews(counts.byArticle[found.id] || 0);
          const all = await listPublishedArticles(8);
          if (alive) setRelated(all.filter((a) => a.id !== found.id).slice(0, 3));
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Impossible de charger cet article.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceScrolled />

      <main className="pt-20 md:pt-24">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : error ? (
          <div className="max-w-3xl mx-auto px-4 py-24 text-center">
            <i className="ri-error-warning-line text-3xl text-accent-600"></i>
            <p className="mt-3 text-sm text-foreground-600">{error}</p>
          </div>
        ) : !article ? (
          <div className="max-w-3xl mx-auto px-4 py-24 text-center">
            <i className="ri-file-search-line text-3xl text-foreground-300"></i>
            <h1 className="mt-4 text-xl font-bold font-heading text-foreground-950">Article introuvable</h1>
            <p className="mt-2 text-sm text-foreground-600">Cet article n’existe pas ou n’est plus publié.</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 mt-5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>Retour au blog
            </Link>
          </div>
        ) : (
          <>
            <div className="w-full h-[260px] md:h-[380px] overflow-hidden bg-background-100">
              <img
                src={article.cover_image || FALLBACK_COVER}
                alt={article.title}
                title={`${article.title} — blog Zifek`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <article className="max-w-3xl mx-auto px-4 md:px-6 -mt-16 md:-mt-24 relative">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-9">
                <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 no-underline mb-4">
                  <i className="ri-arrow-left-line"></i>Blog
                </Link>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {tagList(article.tags).map((t) => (
                    <span key={t} className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold">
                      {t}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">
                  {article.title}
                </h1>

                {article.excerpt && (
                  <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
                )}

                <div className="mt-5 pb-5 border-b border-background-200/70 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {formatArticleDate(article.published_at || article.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {readingMinutes(article.content)} min de lecture
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-accent-600"></i>
                    {views.toLocaleString('fr-FR')} lectures
                  </span>
                </div>

                <div className="mt-6">
                  <ArticleContent content={article.content} />
                </div>
              </div>
            </article>

            {related.length > 0 && (
              <section className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-16">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-5">À lire ensuite</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map((a) => (
                    <Link
                      key={a.id}
                      to={`/blog/${a.slug || a.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden no-underline hover:border-primary-200 transition-colors"
                    >
                      <div className="w-full h-32 overflow-hidden bg-background-100">
                        <img
                          src={a.cover_image || FALLBACK_COVER}
                          alt={a.title}
                          title={`${a.title} — blog Zifek`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                          {a.title}
                        </h3>
                        <p className="mt-1 text-[11px] text-foreground-500">{formatArticleDate(a.published_at || a.created_at)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}